import { orderBy } from 'lodash';
import { catchError, mergeMap, throwError } from "rxjs";
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, Store, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { ApiControlService } from '@medsurf/flat-services';
import { EntityBaseState } from '../base/entity.base.state';
import * as FlatStates from '../../internal';

export const MEDIA_ENTITY_TOKEN = new StateToken<MediaEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.MEDIA_ENTITY);

export type MediaEntityStateModel = FlatModels.EntityStatesModels.EntityStateModel<FlatModels.MediaEntityModels.MediaEntityType>;

@State<MediaEntityStateModel>({
  name: MEDIA_ENTITY_TOKEN,
  defaults: {
    entities: {},
  }
})
@Injectable()
export class MediaEntityState extends EntityBaseState<FlatModels.MediaEntityModels.MediaEntityType, MediaEntityStateModel>{
  constructor(protected store: Store,
              protected apiControlService: ApiControlService) {
    super();
  }

  //<editor-fold desc="Selectors">

  @Selector([MediaEntityState])
  static override typedEntities$(state: MediaEntityStateModel): { [id: string]: FlatModels.MediaEntityModels.MediaEntityType } {
    return EntityBaseState.typedEntities$(state);
  }

  @Selector([MediaEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.MediaEntityModels.MediaEntityType }): FlatModels.MediaEntityModels.MediaEntityType[] {
    return EntityBaseState.entities$(typedEntities);
  }
	
  @Selector([MediaEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.MediaEntityModels.MediaEntityType }) {
    return EntityBaseState.entity$(typedEntities);
  }
	
  @Selector([MediaEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.MediaEntityModels.MediaEntityType }) {
    return EntityBaseState.entitiesByIds$(typedEntities);
  }
	
  @Selector([
    MediaEntityState.typedEntities$,
    FlatStates.ImageEntityState.allCurrentSelectedSlideImages$
  ])
  static allCurrentSelectedSlideMedias$(typedEntities: { [id: string]: FlatModels.MediaEntityModels.MediaEntityType },
                                        allCurrentSelectedSlideImages: FlatModels.ImageEntityModels.Image[]) {
    if (Array.isArray(allCurrentSelectedSlideImages)) {
      return allCurrentSelectedSlideImages.reduce((arr, image) => {
        const entity = typedEntities[image.media as unknown as string];
        if (entity) {
          arr.push(entity);
        }
        return arr;
      }, [] as FlatModels.MediaEntityModels.MediaEntityType[]);
    }
    return [];
  }
	
  @Selector([
    MediaEntityState.typedEntities$,
    FlatStates.ImageEntityState.currentSelectedSlideValidImages$
  ])
  static currentSelectedSlideValidMedias$(typedEntities: { [id: string]: FlatModels.MediaEntityModels.MediaEntityType },
                                          currentSelectedSlideValidImages: FlatModels.ImageEntityModels.Image[]) {
    if (currentSelectedSlideValidImages) {

      const sortedImages = orderBy(currentSelectedSlideValidImages, (a) => a.sequenceNumber || 0, 'asc');

      return sortedImages.reduce<FlatModels.MediaEntityModels.MediaEntityType[]>((arr, image) => {
        const entity = typedEntities[image.media as unknown as string];
        if (entity) arr.push(entity);
        return arr;
      }, []);
    }
    return [];
  }
	
  @Selector([
    MediaEntityState.typedEntities$,
    FlatStates.ImageEntityState.currentSelectedSlideImage$
  ])
  static currentSelectedSlideMedia$(typedEntities: { [id: string]: FlatModels.MediaEntityModels.MediaEntityType },
                                    currentSelectedSlideImage: FlatModels.ImageEntityModels.Image | null) {
    if (currentSelectedSlideImage) {
      return typedEntities[currentSelectedSlideImage.media as unknown as string];
    }
    return null;
  }
	
  @Selector([
    MediaEntityState.typedEntities$,
    FlatStates.PageEntityState.currentSelectedPage$,
  ])
  static currentSelectedPageViewerPdf$(typedEntities: { [id: string]: FlatModels.MediaEntityModels.MediaEntityType },
                                       currentSelectedPage: FlatModels.PageEntityModels.Grid | FlatModels.PageEntityModels.Slide) {
    return currentSelectedPage.pdfMedia ? typedEntities[currentSelectedPage.pdfMedia] || null : null;
  }
	
  @Selector([
    MediaEntityState.typedEntities$,
    FlatStates.PageEntityState.currentSelectedPage$,
  ])
  static currentSelectedPageAuthorPdf$(typedEntities: { [id: string]: FlatModels.MediaEntityModels.MediaEntityType },
                                       currentSelectedPage: FlatModels.PageEntityModels.Grid | FlatModels.PageEntityModels.Slide) {
    return currentSelectedPage.pdfMediaAuthor ? typedEntities[currentSelectedPage.pdfMediaAuthor] || null : null;

  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.MediaEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<MediaEntityStateModel>,
                            action: FlatActions.MediaEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }
	
  @Action(FlatActions.MediaEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<MediaEntityStateModel>,
                              action: FlatActions.MediaEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }
	
  @Action(FlatActions.MediaEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<MediaEntityStateModel>,
                               action: FlatActions.MediaEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }
	
  @Action(FlatActions.MediaEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<MediaEntityStateModel>,
                                 action: FlatActions.MediaEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">
	
  @Action(FlatActions.MediaEntityActions.AddEntity)
  addEntity(_: StateContext<MediaEntityStateModel>,
            {entity}: FlatActions.MediaEntityActions.AddEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.MediaEntityState.addEntity"
      );
    }

    // Add Media
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.MEDIA_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }

  @Action(FlatActions.MediaEntityActions.UpdateEntity)
  updateEntity(_: StateContext<MediaEntityStateModel>,
               {entity}: FlatActions.MediaEntityActions.UpdateEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.MediaEntityState.updateEntity"
      );
    }

    // Update Media
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.MEDIA_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }

  @Action(FlatActions.MediaEntityActions.RemoveEntity)
  removeEntity(_: StateContext<MediaEntityStateModel>,
               {entity}: FlatActions.MediaEntityActions.RemoveEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.MediaEntityState.removeEntity"
      );
    }

    // Remove Media
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.MEDIA_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }
  
  @Action(FlatActions.MediaEntityActions.RearrangeMediaCopyrights)
  rearrangeMediaCopyrights({dispatch}: StateContext<MediaEntityStateModel>,
                           {media, mediaCopyrights}: FlatActions.MediaEntityActions.RearrangeMediaCopyrights) {
    // Iterate
    let i = 0;
    for (const tmpMediaCopyright of mediaCopyrights) {
      // Get Media Copyright
      const mediaCopyright = (media.mediaCopyrights || []).find(mc => mc.id === tmpMediaCopyright.id);
      if (!mediaCopyright) {
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.no_entity_found',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.MediaEntityState.rearrangeMediaCopyrights"
        );
      }
      // Update Entity
      mediaCopyright.order = i++;
    }
    // Update Media
    dispatch(new FlatActions.MediaEntityActions.UpdateEntity(media));
  }

  //</editor-fold>

  //</editor-fold>
}
